import type { StatusOverride } from '../types';

/**
 * **Did this match produce evidence for this hypothesis?** — the one predicate PRE-9 made necessary,
 * in one place because two requirements now depend on it and a second copy is a second chance for
 * them to disagree (DIST-9, §10.2; LOCK-2's cap, §6.6).
 *
 * ── Why it exists at all ────────────────────────────────────────────────────
 *
 * Before revision 4 a match held the handful of hypotheses the user had chosen at Pre-Match, so
 * *"the hypotheses on this match"* and *"the patterns this match saw"* were near enough the same
 * set that no code had to tell them apart. **PRE-9 pre-selects 49**, and the two sets come apart on
 * every match ever created: the board fills with rows nobody has touched, and every consumer that
 * was implicitly reading "what the user cared about" starts reading "what the library contains".
 *
 * Two consumers broke in exactly that way, in the same direction, and both are silent:
 *
 * - **LOCK-2** (§6.6): `topN(8)` at minute 15 would pad the suggested board with never-observed rows
 *   whenever fewer than eight had been tapped — permanent second-half slots under INV-4's
 *   assign-once rule.
 * - **DIST-7 / DIST-9** (§10.2): *"fewer than `TALKPOINT_COUNT` hypotheses **exist**"* became false
 *   of every match ever created, so the source-less escape hatch was unreachable — and the visible
 *   path out of a sparse match became promoting three patterns nobody observed onto the share card
 *   that is the product's entire output (P-3).
 *
 * `changing-a-bound-changes-what-you-persist`, in its inverse: adding rows changed what an existing
 * predicate was measuring, and nothing in the type system or the test suite could see it.
 *
 * ── The definition, and why the override half is not optional ───────────────
 *
 * > *"a hypothesis with `cachedCount = 0` and no user-set status override"*  — DIST-9, verbatim.
 *
 * The count is the primary signal and `statusOverride` is the second, because §3.3 makes the
 * override the user's *deliberate* judgement — a pattern they disproved or invalidated **without**
 * incrementing it is still a pattern they made a decision about, and dropping it from distillation
 * because the counter reads zero would discard the one thing they did say about it.
 *
 * ── It is a READ, never a write ─────────────────────────────────────────────
 *
 * DIST-9: *"the exclusion is from one list, never from the data."* Nothing here deletes, hides or
 * restates a status. An unevidenced hypothesis stays in the match, in the archive (INV-12), in
 * replay (INV-9), in FILE-1's team view and in HT-2's board-management sheet, and the archive's byte
 * output is identical with this filter applied and with it removed.
 */
export interface Evidenced {
  readonly cachedCount: number;
  readonly statusOverride: StatusOverride;
}

/** DIST-9 / §6.6 — `cachedCount > 0`, or a status the user set themselves. */
export function isEvidenced(row: Evidenced): boolean {
  return row.cachedCount > 0 || row.statusOverride !== null;
}
